import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, Skeleton, Tab, Tabs } from '@mui/material';
import { showAxiosMessageError } from '../../utils/error-message.util';
import { useSnackbars } from '../../hooks/snackbar.hook';
import { useLocation, useSearchParams } from 'react-router-dom';
import { getValueLocalStorage, useLocalStorage } from '../../hooks/use-local-storage.hook';
import {
    getMeetingFileContent,
    getMeetingFiles,
    getMeetings,
    MeetingFileMetadataDto,
    updateMeetingAttendance,
} from '../../services/general-meeting.service';
import { MeetingDto, MyProxyGivenToDto } from '../../services/dtos/meeting.dto';
import { a11yProps, TabPanel } from '../../components/commun/panel';
import MeetingFilesUi from '../../components/userInterfaces/meeting-files/meeting-files.ui';
import { FileDto } from '../../services/dtos/file.dto';
import MeetingUi from '../../components/userInterfaces/meeting/meeting.ui';
import CompositionUi from '../../components/userInterfaces/composition/composition.ui';
import DecisionDeliberationUi from '../../components/userInterfaces/decision-deliberation/decision-deliberation.ui';
import MapUi from '../../components/userInterfaces/map/map.ui';
import { UserAccountDto } from '../../services/dtos/user-account.dto';
import { MandateType, TabType } from '../../services/dtos/mandate.dto';
import HoldingsUi from '../../components/userInterfaces/holdings/holdings.ui';
import { MandateGeneralDto } from '../../services/dtos/mandate-general.dto';
import { MandateEstablishmentDto } from '../../services/dtos/mandate-establishment-dto';
import { MandateTerritoryDto } from '../../services/dtos/mandate-territory.dto';
import TechnicalIndicatorsUi from '../../components/userInterfaces/technical-indicators/technical-indicators.ui';
import FinancesUi from '../../components/userInterfaces/finances/finances.ui';
import RelationUi from '../../components/userInterfaces/relation/relation.ui';
import DocumentsUi from '../../components/userInterfaces/documents/documents.ui';
import AssignmentsUi from '../../components/userInterfaces/assignments/assignments.ui';
import CommissionsUi from '../../components/userInterfaces/commissions/commissions.ui';
import { CommissionDto } from '../../services/dtos/commission.dto';
import { filterTabsByAccessRole, resolveMandateAccessRole } from '../../utils/access-role.util';
import NoData from '../../components/commun/no-data';
import { getCurrentAccountUser } from '../../services/user.service';
import './mandate.page.scss';

/**
 * Page de compte de l'utilisateur
 * @constructor
 */
export default function MandatePage() {
    //SnackBar pour les messages
    const showMessage = useSnackbars();
    // Liste des tabs
    const [tabs, setTabs] = useState<TabType[]>();
    // valueTab en cours
    const [valueTab, setValueTab] = useState(0);
    // Liste des tabs
    const [commission, setCommission] = useState<CommissionDto>();
    // Documents du meeting choisi
    const [files, setFiles] = useState<Array<FileDto>>();
    // Ensemble des documents de l'assemblée générale
    const [allMeetingFiles, setAllMeetingFiles] = useState<Array<FileDto>>([]);
    //Loading pour les fichiers
    const [loading, setLoading] = useState<boolean>(false);
    // Meeting choisi
    const [meeting, setMeeting] = useState<MeetingDto>();
    // Affichage de la popup des documents de la réunion sélectionnée
    const [meetingFilesDialogOpen, setMeetingFilesDialogOpen] = useState(false);
    // Meeting choisi
    const [mandate, setMandate] = useState<MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto>();
    // Meeting choisi
    const [mandateType, setMandateType] = useState<string | null>();
    // Rôle d'accès au mandat courant
    const [accessRole, setAccessRole] = useState<string>();
    // récupération des params url
    const [searchParams] = useSearchParams();
    const searchParamsKey = searchParams.toString();
    // récupération des user
    const [user, setUser] = useLocalStorage<UserAccountDto | null>('userAccount', null);
    const connectedUser = useMemo(() => getValueLocalStorage('user'), []);
    const [accountHydrated, setAccountHydrated] = useState(false);
    // récupération locations
    const location = useLocation();
    // index à rajouter quand on a le tab des documents de réunions
    let newIndexTab = 0;
    // index à rajouter quand on a le tab des documents de réunions
    let newIndexTabContent = 0;
    const isGeneralAssembly = mandateType === MandateType.AG;
    const isOfficeAdmin = mandateType === MandateType.BSCA;
    const resolvedUser = user ?? ({} as UserAccountDto);

    useEffect(() => {
        let isMounted = true;

        if (!connectedUser?.token) {
            setAccountHydrated(true);
            return;
        }

        setAccountHydrated(false);
        getCurrentAccountUser()
            .then(result => {
                if (!isMounted) {
                    return;
                }

                setUser(result);
            })
            .catch(error => {
                console.error("Erreur lors de la récupération de l'utilisateur");
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                if (isMounted) {
                    setAccountHydrated(true);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [connectedUser?.token, setUser, showMessage]);

    // Récupération du mandat
    useEffect(() => {
        if (!accountHydrated) {
            return;
        }

        const mandateId = searchParams.get('m');
        const establishmentId = searchParams.get('e');
        const mandateName = getMandateFromPath(location.pathname, mandateId, establishmentId, resolvedUser);
        setValueTab(0);
        setCommission(undefined);
        setFiles(undefined);
        setAllMeetingFiles([]);
        setMeeting(undefined);
        setMandate(undefined);
        setMandateType(mandateName);
        //Récupération le mandat courant courant et inscrire les onglets à afficher
        let nextTabs: TabType[] = [];
        let nextMandate: MandateGeneralDto | MandateEstablishmentDto | MandateTerritoryDto | undefined;

        switch (mandateName) {
            // Assemblée général
            case MandateType.AG:
                nextTabs = [TabType.MEETING, TabType.MEETINGFILE, TabType.COMPOSITION];
                nextMandate = resolvedUser.mandateAssGeneral;
                break;
            // Bureau syndical
            case MandateType.BSCA:
                nextTabs = [TabType.MEETING, TabType.MEETINGFILE, TabType.COMPOSITION];
                nextMandate = resolvedUser.mandateOfficeAdmin;
                break;
            // Territoire
            case MandateType.TERRITOIRE:
                // TabType.MAP masqué temporairement (onglet Cartes ne retourne aucun document)
                nextTabs = [TabType.COMPOSITION, TabType.INDICATOR, TabType.RELATION, TabType.MEETING, TabType.MEETINGFILE];
                resolvedUser?.mandatesTerritory?.forEach((value: MandateTerritoryDto) => {
                    if (value.id.toString() === mandateId) {
                        nextMandate = value;
                    }
                });
                break;
            // COPE eau / Assainissement
            case MandateType.ASSAINISSEMENT:
            case MandateType.EAU:
                nextTabs = [
                    TabType.COMPOSITION,
                    TabType.INDICATOR,
                    TabType.FINANCES,
                    TabType.RELATION,
                    TabType.DECISIONDELIBERATION,
                    TabType.DOCUMENTS,
                    TabType.MEETING,
                    TabType.MEETINGFILE,
                    TabType.ASSIGNMENTS,
                ];
                resolvedUser?.mandatesEstablishment?.forEach((value: MandateEstablishmentDto) => {
                    if (value.id.toString() === mandateId && value.establishmentId.toString() === establishmentId) {
                        nextMandate = value;
                    }
                });
                break;
            // Bassin
            case MandateType.BASSIN:
                nextTabs = [
                    TabType.COMPOSITION,
                    TabType.MAP,
                    TabType.INDICATOR,
                    TabType.DECISIONDELIBERATION,
                    TabType.DOCUMENTS,
                    TabType.MEETING,
                    TabType.MEETINGFILE,
                ];
                resolvedUser?.mandatesBasin?.forEach((value: MandateGeneralDto) => {
                    if (value.id.toString() === mandateId) {
                        nextMandate = value;
                    }
                });
                break;
            // Bassin
            case MandateType.COMMISSIONS:
                nextTabs = [TabType.COMMISSIONS, TabType.MEETING, TabType.MEETINGFILE];
                nextMandate = resolvedUser.mandateCommissions ?? resolvedUser.mandateAssGeneral;
                break;
            default:
                break;
        }

        setMandate(nextMandate);
        const resolvedAccessRole = resolveMandateAccessRole(nextMandate, connectedUser, resolvedUser);
        setAccessRole(resolvedAccessRole);
        setTabs(filterTabsByAccessRole(nextTabs, resolvedAccessRole));

        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountHydrated, connectedUser, location.pathname, resolvedUser, searchParamsKey]);

    /**
     * Récupère le type du mandate en fonction du chemin fourni, de l'ID de mandat, de l'ID d'établissement et du compte utilisateur.
     *
     * @param {string} path - Le chemin actuel
     * @param {string|null} mandateId - L'ID du mandat
     * @param {string|null} establishmentId - L'ID de l'établissement
     * @param {UserAccountDto} userAccount - L'objet du compte utilisateur
     * @returns {string|null} - Le sous-titre ou null si aucun sous-titre n'est trouvé
     */
    const getMandateFromPath = (
        path: string,
        mandateId: string | null,
        establishmentId: string | null,
        userAccount: UserAccountDto
    ): string | null => {
        switch (path) {
            case '/elu/ag':
                return MandateType.AG;
            case '/elu/bsca':
                return MandateType.BSCA;
            case '/elu/commissions':
                return MandateType.COMMISSIONS;
            case '/elu/territoire':
                return MandateType.TERRITOIRE;
            default:
                if (
                    mandateId &&
                    path?.includes('elu/eau') &&
                    userAccount?.mandatesEstablishment?.find(
                        value =>
                            value.id.toString() === mandateId && value.establishmentId.toString() === establishmentId
                    )
                ) {
                    return MandateType.EAU;
                }

                if (
                    mandateId &&
                    path?.includes('elu/assainissement') &&
                    userAccount?.mandatesEstablishment?.find(
                        value =>
                            value.id.toString() === mandateId && value.establishmentId.toString() === establishmentId
                    )
                ) {
                    return MandateType.ASSAINISSEMENT;
                }

                if (
                    mandateId &&
                    path?.includes('elu/bassin') &&
                    userAccount?.mandatesBasin?.find(value => value.id.toString() === mandateId)
                ) {
                    return MandateType.BASSIN;
                }

                return null;
        }
    };

    const getActiveMandateId = (
        path: string,
        mandateId: string | null,
        establishmentId: string | null,
        userAccount: UserAccountDto
    ): string | null => {
        switch (path) {
            case '/elu/ag':
                return userAccount?.mandateAssGeneral?.id ?? null;
            case '/elu/commissions':
                return userAccount?.mandateCommissions?.id ?? userAccount?.mandateAssGeneral?.id ?? null;
            case '/elu/bsca':
                return userAccount?.mandateOfficeAdmin?.id ?? null;
            case '/elu/territoire':
            case '/elu/bassin':
                return mandateId;
            default:
                if (
                    mandateId &&
                    establishmentId &&
                    (path?.includes('elu/eau') || path?.includes('elu/assainissement'))
                ) {
                    return mandateId;
                }

                return null;
        }
    };

    const getTabIndex = (tabType: TabType) => tabs?.findIndex(value => tabType === value) ?? 0;

    const getMeetingFilesMetadata = async (paramMeeting: MeetingDto, mandateId: string): Promise<Array<FileDto>> => {
        const result = await getMeetingFiles(paramMeeting.id, mandateId);
        if (!result) {
            return [];
        }

        return result
            .filter(
                (
                    doc: MeetingFileMetadataDto
                ): doc is Required<Pick<MeetingFileMetadataDto, 'documentId' | 'fileName'>> =>
                    !!doc.documentId && !!doc.fileName
            )
            .map(doc => ({
                name: doc.fileName,
                data: doc.documentId,
                mandateId,
                meetingId: paramMeeting.id,
                meetingDate: paramMeeting.date,
                meetingStartTime: paramMeeting.startTime,
                meetingLabel: `${paramMeeting.date} à ${paramMeeting.startTime}`,
            }));
    };

    const getAllMeetingsByStatus = async (mandateId: string, status: string): Promise<Array<MeetingDto>> => {
        const itemsPerPage = 50;
        let page = 0;
        let totalItems = 0;
        let items: Array<MeetingDto> = [];

        do {
            const result = await getMeetings(mandateId, status, page, itemsPerPage);
            items = [...items, ...(result?.items ?? [])];
            totalItems = result?.totalItems ?? items.length;
            page = page + 1;
        } while (items.length < totalItems);

        return items;
    };

    const getMeetingById = async (mandateId: string, meetingId: string): Promise<MeetingDto | undefined> => {
        const upcomingMeetings = await getAllMeetingsByStatus(mandateId, 'Upcoming');
        const upcomingMeeting = upcomingMeetings.find(currentMeeting => currentMeeting.id === meetingId);
        if (upcomingMeeting) {
            return upcomingMeeting;
        }

        const passedMeetings = await getAllMeetingsByStatus(mandateId, 'Passed');
        return passedMeetings.find(currentMeeting => currentMeeting.id === meetingId);
    };

    const loadAllMeetingFiles = async (mandateId: string) => {
        setLoading(true);
        try {
            const upcomingMeetings = await getAllMeetingsByStatus(mandateId, 'Upcoming');
            const passedMeetings = await getAllMeetingsByStatus(mandateId, 'Passed');
            const meetingsWithDocuments = [...upcomingMeetings, ...passedMeetings].filter(
                currentMeeting => currentMeeting.hasDocument
            );
            const documents = (
                await Promise.all(
                    meetingsWithDocuments.map(currentMeeting => getMeetingFilesMetadata(currentMeeting, mandateId))
                )
            ).flat();

            setAllMeetingFiles(documents);
            setFiles(documents);
            setMeeting(undefined);
        } catch (error) {
            console.error('Erreur lors de la récupération des documents administratifs');
            showAxiosMessageError(error as Error, showMessage);
        } finally {
            setLoading(false);
        }
    };

    const openMeetingFromQuery = async (meetingId: string, mandateId: string) => {
        setLoading(true);
        try {
            const meetingFromQuery = await getMeetingById(mandateId, meetingId);
            if (!meetingFromQuery) {
                showMessage.errorAlert('Impossible de retrouver la reunion demandee.');
                return;
            }

            const result = await getMeetingFilesMetadata(meetingFromQuery, mandateId);
            setFiles(result);
            setMeeting(meetingFromQuery);
            setValueTab(getTabIndex(TabType.MEETINGFILE));
        } catch (error) {
            console.error('Erreur lors de l\'ouverture de la réunion depuis le lien');
            showAxiosMessageError(error as Error, showMessage);
        } finally {
            setLoading(false);
        }
    };

    const handlerLoadFileContent = async (paramFile: FileDto): Promise<FileDto | undefined> => {
        if (paramFile.content || !paramFile.meetingId || !paramFile.mandateId) {
            return paramFile;
        }

        try {
            const content = await getMeetingFileContent(paramFile.meetingId, paramFile.mandateId, paramFile.data);
            const updatedFile = { ...paramFile, content };

            setFiles(
                currentFiles =>
                    currentFiles?.map(file =>
                        file.data === updatedFile.data && file.meetingId === updatedFile.meetingId ? updatedFile : file
                    ) ?? []
            );
            setAllMeetingFiles(
                currentFiles =>
                    currentFiles?.map(file =>
                        file.data === updatedFile.data && file.meetingId === updatedFile.meetingId ? updatedFile : file
                    ) ?? []
            );

            return updatedFile;
        } catch (error) {
            console.error('Erreur lors du chargement du document');
            showAxiosMessageError(error as Error, showMessage);
            return undefined;
        }
    };

    //Permet de gérer sur quelle vue de tab on est.
    const handlerChangeValueTab = (_event: React.SyntheticEvent, newValue: number) => {
        if (isGeneralAssembly && newValue === getTabIndex(TabType.MEETINGFILE)) {
            setFiles(allMeetingFiles);
            setMeeting(undefined);

            const currentMandateId = getActiveMandateId(
                location.pathname,
                searchParams.get('m'),
                searchParams.get('e'),
                resolvedUser
            );
            if (currentMandateId && allMeetingFiles.length === 0) {
                void loadAllMeetingFiles(currentMandateId);
            }
        }
        setValueTab(newValue);
    };

    //Permet d'ouvrir la popup des documents d'une réunion, comme sur la home page
    const handlerGetMeetingFiles = async (paramMeeting: MeetingDto) => {
        const currentMandateId = getActiveMandateId(
            location.pathname,
            searchParams.get('m'),
            searchParams.get('e'),
            resolvedUser
        );
        if (!currentMandateId) {
            showMessage.errorAlert('Impossible de retrouver le mandat associe a cette reunion.');
            return;
        }

        setMeeting(paramMeeting);
        setMeetingFilesDialogOpen(true);
        setLoading(true);
        await getMeetingFilesMetadata(paramMeeting, currentMandateId)
            .then(result => {
                setFiles(result);
            })
            .catch(error => {
                console.error('Erreur lors de la récupération des documents');
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handlerCloseMeetingFilesDialog = () => {
        setMeetingFilesDialogOpen(false);
    };

    useEffect(() => {
        const requestedTab = searchParams.get('tab');
        if (requestedTab !== 'meetings' || !tabs?.includes(TabType.MEETING)) {
            return;
        }

        setValueTab(getTabIndex(TabType.MEETING));
    }, [tabs, searchParamsKey]);

    useEffect(() => {
        const meetingId = searchParams.get('meeting');
        if (!meetingId || !mandate || meeting?.id === meetingId) {
            return;
        }

        const currentMandateId = getActiveMandateId(
            location.pathname,
            searchParams.get('m'),
            searchParams.get('e'),
            resolvedUser
        );
        if (!currentMandateId || !tabs?.includes(TabType.MEETINGFILE)) {
            return;
        }

        void openMeetingFromQuery(meetingId, currentMandateId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mandate, tabs, searchParamsKey, location.pathname]);

    const handlerShowAllMeetingFiles = () => {
        setFiles(allMeetingFiles);
        setMeeting(undefined);
    };

    const handlerUpdateMeetingAttendance = async (
        currentMeeting: MeetingDto,
        attendance: boolean
    ): Promise<MeetingDto> => {
        try {
            const updatedAttendance = await updateMeetingAttendance(currentMeeting.id, attendance);
            const updatedMeeting = {
                ...currentMeeting,
                attendance: updatedAttendance,
            };

            setMeeting(previousMeeting =>
                previousMeeting?.id === updatedMeeting.id ? updatedMeeting : previousMeeting
            );

            return updatedMeeting;
        } catch (error) {
            console.error('Erreur lors de la mise à jour de la réponse de présence');
            showAxiosMessageError(error as Error, showMessage);
            throw error;
        }
    };

    const handlerProxyChange = (currentMeeting: MeetingDto, myProxyGivenTo: MyProxyGivenToDto | null) => {
        setMeeting(previousMeeting =>
            previousMeeting?.id === currentMeeting.id ? { ...previousMeeting, myProxyGivenTo } : previousMeeting
        );
    };

    //Permet de gérer sur quelle vue de tab on est.
    const handlerCommissionShowMeetings = async (commissionForMeetings: CommissionDto) => {
        // Mise à jour des useState
        setCommission(commissionForMeetings);
        const tabDoc = tabs?.findIndex(value => TabType.MEETING === value);
        setValueTab(tabDoc ?? 0);
    };

    //Conditionnement de la vue, plusieurs interfaces disponibles
    return (
        //Affichage de tabs
        //Gestion des contenus des différents tab
        <div className={`mandatePage${isGeneralAssembly ? ' mandatePage--generalAssembly' : ''}`}>
            <Tabs
                className="mandatePage__tabs"
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                value={valueTab}
                onChange={handlerChangeValueTab}
                sx={{ marginBottom: isGeneralAssembly ? '18px' : '10px' }}
            >
                {tabs?.map((key, index) => {
                    const finalIndex = index - newIndexTab;
                    let tab = undefined;
                    switch (key) {
                        //Tab : Réunions
                        case TabType.MEETING:
                            if (
                                mandateType !== MandateType.COMMISSIONS ||
                                (mandateType === MandateType.COMMISSIONS && commission !== undefined)
                            ) {
                                tab = (
                                    <Tab
                                        className="mandatePage__tab"
                                        label="Réunions"
                                        {...a11yProps(finalIndex)}
                                        key={key}
                                    />
                                );
                            } else {
                                newIndexTab = 1;
                            }
                            break;
                        //Tab : Documents administratifs
                        case TabType.MEETINGFILE:
                            if (isGeneralAssembly || isOfficeAdmin || (files && meeting)) {
                                tab = (
                                    <Tab
                                        className="mandatePage__tab"
                                        label="Documents administratifs"
                                        {...a11yProps(finalIndex)}
                                        key={key}
                                    />
                                );
                            } else {
                                newIndexTab = 1;
                            }
                            break;
                        //Tab : Patrimoine
                        case TabType.HOLDINGS:
                            tab = (
                                <Tab
                                    className="mandatePage__tab"
                                    label="Patrimoine"
                                    {...a11yProps(finalIndex)}
                                    key={key}
                                />
                            );
                            break;
                        //Tab : Composition
                        case TabType.COMPOSITION:
                            tab = (
                                <Tab
                                    className="mandatePage__tab"
                                    label="Composition"
                                    {...a11yProps(finalIndex)}
                                    key={key}
                                />
                            );
                            break;
                        //Tab : Décisions & délibérations
                        case TabType.DECISIONDELIBERATION:
                            tab = (
                                <Tab
                                    className="mandatePage__tab"
                                    label="Décisions & délibérations"
                                    {...a11yProps(finalIndex)}
                                    key={key}
                                />
                            );
                            break;
                        //Tab : Cartes
                        case TabType.MAP:
                            tab = (
                                <Tab className="mandatePage__tab" label="Cartes" {...a11yProps(finalIndex)} key={key} />
                            );
                            break;
                        //Tab : Indicateurs techniques
                        case TabType.INDICATOR:
                            tab = (
                                <Tab
                                    className="mandatePage__tab"
                                    label="Indicateurs techniques"
                                    {...a11yProps(finalIndex)}
                                    key={key}
                                />
                            );
                            break;
                        //Tab : Finances
                        case TabType.FINANCES:
                            tab = (
                                <Tab
                                    className="mandatePage__tab"
                                    label="Finances"
                                    {...a11yProps(finalIndex)}
                                    key={key}
                                />
                            );
                            break;
                        //Tab : Relation abonnés
                        case TabType.RELATION:
                            tab = (
                                <Tab
                                    className="mandatePage__tab"
                                    label="Relation abonnés"
                                    {...a11yProps(finalIndex)}
                                    key={key}
                                />
                            );
                            break;
                        //Tab : Documents
                        case TabType.DOCUMENTS:
                            tab = (
                                <Tab
                                    className="mandatePage__tab"
                                    label="Documents"
                                    {...a11yProps(finalIndex)}
                                    key={key}
                                />
                            );
                            break;
                        //Tab : Proposals d'affectations
                        case TabType.ASSIGNMENTS:
                            tab = (
                                <Tab
                                    className="mandatePage__tab"
                                    label="Propositions d'affectations"
                                    {...a11yProps(finalIndex)}
                                    key={key}
                                />
                            );
                            break;
                        //Tab : Proposals d'affectations
                        case TabType.COMMISSIONS:
                            tab = (
                                <Tab
                                    className="mandatePage__tab"
                                    label="Commissions"
                                    {...a11yProps(finalIndex)}
                                    key={key}
                                />
                            );
                            break;
                    }
                    return tab;
                })}
            </Tabs>
            {tabs?.map((key, index) => {
                const finalIndex = index - newIndexTabContent;
                let tab = undefined;
                switch (key) {
                    //Vue : Réunions
                    case TabType.MEETING:
                        if (
                            mandateType !== MandateType.COMMISSIONS ||
                            (mandateType === MandateType.COMMISSIONS && commission !== undefined)
                        ) {
                            tab = (
                                <TabPanel value={valueTab} index={finalIndex} key={key}>
                                    <MeetingUi
                                        mandate={mandate}
                                        commission={commission}
                                        handlerGetMeetingFiles={handlerGetMeetingFiles}
                                        handlerUpdateMeetingAttendance={handlerUpdateMeetingAttendance}
                                    />
                                </TabPanel>
                            );
                        } else {
                            newIndexTabContent = 1;
                        }
                        break;
                    //Vue : Documents administratifs
                    case TabType.MEETINGFILE:
                        if (isGeneralAssembly || isOfficeAdmin || files) {
                            tab = (
                                <TabPanel value={valueTab} index={finalIndex} key={key}>
                                    {!loading ? files ? (
                                        <MeetingFilesUi
                                            meeting={meeting}
                                            files={files ?? []}
                                            searching={loading}
                                            handlerLoadFileContent={handlerLoadFileContent}
                                            handlerUpdateMeetingAttendance={handlerUpdateMeetingAttendance}
                                            handlerProxyChange={handlerProxyChange}
                                            handlerShowAllMeetingFiles={
                                                isGeneralAssembly ? handlerShowAllMeetingFiles : undefined
                                            }
                                        />
                                    ) : (
                                        <NoData
                                            title="Information :"
                                            content="Sélectionnez une réunion contenant des documents pour les afficher."
                                        />
                                    ) : (
                                        <Skeleton variant="rectangular" height={250}></Skeleton>
                                    )}
                                </TabPanel>
                            );
                        } else {
                            newIndexTabContent = 1;
                        }
                        break;
                    //Vue : Patrimoine
                    case TabType.HOLDINGS:
                        tab = (
                            <TabPanel value={valueTab} index={finalIndex} key={key}>
                                {mandate && <HoldingsUi mandate={mandate} />}
                            </TabPanel>
                        );
                        break;
                    //Vue : Composition
                    case TabType.COMPOSITION:
                        tab = (
                            <TabPanel value={valueTab} index={finalIndex} key={key}>
                                {mandate && <CompositionUi mandate={mandate} mandateType={mandateType} accessRole={accessRole} />}
                            </TabPanel>
                        );
                        break;
                    //Vue : Décisions & délibérations
                    case TabType.DECISIONDELIBERATION:
                        tab = (
                            <TabPanel value={valueTab} index={finalIndex} key={key}>
                                {mandate && <DecisionDeliberationUi mandate={mandate} />}
                            </TabPanel>
                        );
                        break;
                    //Vue : Cartes
                    case TabType.MAP:
                        tab = (
                            <TabPanel value={valueTab} index={finalIndex} key={key}>
                                {mandate && <MapUi mandate={mandate} />}
                            </TabPanel>
                        );
                        break;
                    //Vue : Indicateurs techniques
                    case TabType.INDICATOR:
                        tab = (
                            <TabPanel value={valueTab} index={finalIndex} key={key}>
                                {mandate && <TechnicalIndicatorsUi mandate={mandate} mandateType={mandateType} />}
                            </TabPanel>
                        );
                        break;
                    //Vue : Finances
                    case TabType.FINANCES:
                        tab = (
                            <TabPanel value={valueTab} index={finalIndex} key={key}>
                                {mandate && <FinancesUi mandate={mandate} />}
                            </TabPanel>
                        );
                        break;
                    //Vue : Relation abonnés
                    case TabType.RELATION:
                        tab = (
                            <TabPanel value={valueTab} index={finalIndex} key={key}>
                                {mandate && <RelationUi mandate={mandate} />}
                            </TabPanel>
                        );
                        break;
                    //Vue : Documents
                    case TabType.DOCUMENTS:
                        tab = (
                            <TabPanel value={valueTab} index={finalIndex} key={key}>
                                {mandate && <DocumentsUi mandate={mandate} />}
                            </TabPanel>
                        );
                        break;
                    //Vue : Documents
                    case TabType.ASSIGNMENTS:
                        tab = (
                            <TabPanel value={valueTab} index={finalIndex} key={key}>
                                {mandate && <AssignmentsUi mandate={mandate} />}
                            </TabPanel>
                        );
                        break;
                    //Vue : Documents
                    case TabType.COMMISSIONS:
                        tab = (
                            <TabPanel value={valueTab} index={finalIndex} key={key}>
                                {<CommissionsUi handlerCommissionShowMeetings={handlerCommissionShowMeetings} />}
                            </TabPanel>
                        );
                        break;
                }
                return tab;
            })}
            <Dialog
                open={meetingFilesDialogOpen}
                onClose={handlerCloseMeetingFilesDialog}
                maxWidth="lg"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        maxHeight: '92vh',
                        overflow: 'hidden',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        overflowY: 'hidden',
                        padding: { xs: 2, sm: 3 },
                    }}
                >
                    {meeting && (
                        <MeetingFilesUi
                            meeting={meeting}
                            files={files ?? []}
                            searching={loading}
                            handlerLoadFileContent={handlerLoadFileContent}
                            handlerUpdateMeetingAttendance={handlerUpdateMeetingAttendance}
                            handlerProxyChange={handlerProxyChange}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
